<template>
    <kiosk-team-details :teamId="props.teamId">
        <div class="flex flex-col w-full justify-center">
            <page-header>Groups</page-header>

            <vx-alert v-if="linkedViewFailed" color="danger" :closeable="false" data-test="load-failed">
                <div class="flex items-center justify-between">
                    The saved view could not be loaded.
                    <vx-button size="sm" color="danger" @click="loadLinkedView" data-test="retry-load">
                        Retry <font-awesome-icon widthAuto :icon="faRefresh" />
                    </vx-button>
                </div>
            </vx-alert>
            <kiosk-team-groups-table v-else-if="linkedViewResolved" :teamId="props.teamId" :initialView="linkedView" />
        </div>
    </kiosk-team-details>
</template>

<script lang="ts" setup>
import { faRefresh } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxButton } from '@voxie/frontend-components';
import { onMounted, ref } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import { useQueryParams } from '~/composables/useQueryString';
import { useToasted } from '~/composables/useToasted';
import { adminTeamGroupViewsClient } from '~/services';
import type { KioskTeamGroupView } from '~/services/adminTeamGroupViewsClient.types';
import KioskTeamDetails from '../details/KioskTeamDetails.vue';
import KioskTeamGroupsTable from './KioskTeamGroupsTable.vue';
import { KIOSK_TEAM_GROUP_VIEW_UNAVAILABLE_MESSAGE, isViewNotFound } from './kioskTeamGroupsTableHelpers';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
});

const toasted = useToasted();
const urlQuery = useQueryParams<{ view?: unknown }>();

const linkedViewId = typeof urlQuery.view === 'number' ? urlQuery.view : null;
const linkedView = ref<KioskTeamGroupView | null>(null);
const linkedViewResolved = ref(linkedViewId === null);
const linkedViewFailed = ref(false);

const loadLinkedView = async () => {
    if (linkedViewId === null) {
        return;
    }

    linkedViewFailed.value = false;

    try {
        const { data } = await adminTeamGroupViewsClient.getView(props.teamId, linkedViewId);

        linkedView.value = data;
    } catch (error) {
        if (!isViewNotFound(error)) {
            linkedViewFailed.value = true;
            return;
        }

        toasted.error(KIOSK_TEAM_GROUP_VIEW_UNAVAILABLE_MESSAGE);
    }

    linkedViewResolved.value = true;
};

onMounted(loadLinkedView);
</script>
